import isNil from 'lodash/isNil'

interface AxiosLikeError {
  message?: string
  response?: {
    status?: number
    data?: { message?: unknown; error?: string }
  }
}

/**
 * Shown instead of a guard's own wording. A refusal at the door says nothing
 * about what was sent, and in a bulk flow it is stamped on every recipient,
 * where "Forbidden resource" reads as a verdict on those people.
 */
export const AUTH_FAILURE_MESSAGE =
  'Your session could not be verified, so nothing was changed. Sign in again, then retry.'

/** Nest's default bodies for a guard refusal, which name no cause. */
const GENERIC_AUTH_DETAILS = new Set(['forbidden resource', 'unauthorized'])

/**
 * Only a refusal with nothing to say is replaced: a 403 that carries a real
 * explanation, such as a feature being off for a customer, keeps its own.
 */
const isGenericAuthRefusal = (
  status: number | undefined,
  detail: unknown
): boolean => {
  if (status !== 401 && status !== 403) return false
  if (isNil(detail)) return true
  return (
    typeof detail === 'string' &&
    GENERIC_AUTH_DETAILS.has(detail.trim().toLowerCase())
  )
}

/**
 * Pulls a readable message out of an axios error. Nest returns validation
 * failures as a string array, a single string for thrown exceptions, and
 * sometimes only the `error` field, so all three shapes are handled.
 *
 * A request that never got a response is reported as such rather than as the
 * caller's fallback: "the server rejected this" and "the server was not there"
 * need different fixes, and in bulk flows the message is the only thing the
 * operator sees for every row in the failed batch.
 */
export const extractError = (error: unknown, fallback: string): string => {
  const { message, response } = (error ?? {}) as AxiosLikeError
  const data = response?.data
  const detail = data?.message

  if (isGenericAuthRefusal(response?.status, detail)) {
    return AUTH_FAILURE_MESSAGE
  }

  if (Array.isArray(detail)) {
    const parts = detail.flatMap((part) => {
      if (typeof part === 'string') return [part]
      // Some endpoints report validation per field: { field, errors: [...] }.
      const fieldErrors = (part as { errors?: unknown })?.errors
      return Array.isArray(fieldErrors)
        ? fieldErrors.filter((each): each is string => typeof each === 'string')
        : []
    })
    if (parts.length > 0) return parts.join(' · ')
  }
  if (typeof detail === 'string') return detail
  if (typeof data?.error === 'string') return data.error

  if (isNil(response)) {
    return message === 'Network Error'
      ? 'Could not reach the server'
      : (message ?? fallback)
  }

  return isNil(response.status)
    ? fallback
    : `${fallback} (HTTP ${response.status})`
}

/**
 * True when the server rejected the request itself (4xx) rather than failing to
 * serve it. Such a rejection is a property of the payload, so a bulk caller can
 * report it once instead of once per recipient.
 */
export const isRequestRejected = (error: unknown): boolean => {
  const status = ((error ?? {}) as AxiosLikeError).response?.status
  return !isNil(status) && status >= 400 && status < 500
}
